import React from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useCheckoutStore } from "../store/checkout-store";
import { useTranslation } from "react-i18next";
import { X, HelpCircle } from "lucide-react";
import { PaymentBrandLogo } from "@/features/settings/components/PaymentBrandLogo";
import {
  validateCardNumber,
  validateExpirationDate,
  validateCVV,
  validateNameOnCard,
  formatCardNumberWithCursor,
  formatExpirationDate,
  getCardBrand,
} from "../utils/validation";

export const PaymentMethodModal: React.FC = () => {
  const { t } = useTranslation();
  const {
    isPaymentMethodModalOpen,
    editingPaymentMethodId,
    paymentMethods,
    closePaymentMethodModal,
    addPaymentMethod,
    updatePaymentMethod,
  } = useCheckoutStore();

  const editingPaymentMethod = editingPaymentMethodId
    ? paymentMethods.find((pm) => pm.id === editingPaymentMethodId)
    : null;

  const isEditMode = !!editingPaymentMethodId;
  const [isSaving, setIsSaving] = React.useState(false);
  const [saveCardDetails, setSaveCardDetails] = React.useState(true);

  // Form state - editingPaymentMethod değiştiğinde güncelle
  const [cardNumber, setCardNumber] = React.useState("");
  const [expirationDate, setExpirationDate] = React.useState("");
  const [cvv, setCvv] = React.useState("");
  const [nameOnCard, setNameOnCard] = React.useState("");

  // Validation errors state
  const [errors, setErrors] = React.useState<{
    cardNumber?: string;
    expirationDate?: string;
    cvv?: string;
    nameOnCard?: string;
  }>({});

  // Touched fields - hangi alanlar değiştirildi (ilk focus sonrası)
  const [touched, setTouched] = React.useState<{
    cardNumber: boolean;
    expirationDate: boolean;
    cvv: boolean;
    nameOnCard: boolean;
  }>({
    cardNumber: false,
    expirationDate: false,
    cvv: false,
    nameOnCard: false,
  });

  // Edit mode'da form'u doldur
  React.useEffect(() => {
    if (editingPaymentMethod) {
      setCardNumber(`**** **** **** ${editingPaymentMethod.last4}`);
      setExpirationDate(
        `${editingPaymentMethod.exp_month
          .toString()
          .padStart(2, "0")}/${editingPaymentMethod.exp_year
          .toString()
          .slice(-2)}`
      );
      setNameOnCard(editingPaymentMethod.email || "");
    } else {
      // Add mode - form'u temizle
      setCardNumber("");
      setExpirationDate("");
      setCvv("");
      setNameOnCard("");
    }
    // Modal açıldığında/editingPaymentMethod değiştiğinde error'ları temizle
    setErrors({});
    setTouched({
      cardNumber: false,
      expirationDate: false,
      cvv: false,
      nameOnCard: false,
    });
  }, [editingPaymentMethod, isPaymentMethodModalOpen]);

  // Detect card brand from card number (or use existing brand in edit mode)
  const detectedBrand = React.useMemo(() => {
    // Edit mode'da mevcut brand'i kullan
    if (isEditMode && editingPaymentMethod) {
      return editingPaymentMethod.brand.toLowerCase();
    }
    // Add mode'da card number'dan detect et
    return getCardBrand(cardNumber);
  }, [cardNumber, isEditMode, editingPaymentMethod]);

  // Real-time validation helper
  const validateField = (
    field: "cardNumber" | "expirationDate" | "cvv" | "nameOnCard",
    value: string
  ) => {
    if (!touched[field]) return; // Henüz touch edilmemişse validation yapma

    let error: string | undefined;

    switch (field) {
      case "cardNumber": {
        if (!value || value.trim().length === 0) {
          error = "REQUIRED";
        } else {
          const digits = value.replace(/\s/g, "").replace(/\*/g, "");
          if (digits.length < 13 || digits.length > 19) {
            error = "INVALID_LENGTH";
          } else if (!isEditMode && !validateCardNumber(value)) {
            // Edit mode'da masked card number olduğu için Luhn kontrolü yapma
            error = "INVALID_NUMBER";
          }
        }
        break;
      }
      case "expirationDate": {
        const expValidation = validateExpirationDate(value);
        if (!expValidation.isValid) {
          error = expValidation.error;
        }
        break;
      }
      case "cvv": {
        const cvvValidation = validateCVV(value, detectedBrand);
        if (!cvvValidation.isValid) {
          error = cvvValidation.error;
        }
        break;
      }
      case "nameOnCard": {
        const nameValidation = validateNameOnCard(value);
        if (!nameValidation.isValid) {
          error = nameValidation.error;
        }
        break;
      }
    }

    setErrors((prev) => ({
      ...prev,
      [field]: error,
    }));
  };

  // Get error message for translation
  const getErrorMessage = (field: string, errorKey?: string): string | null => {
    if (!errorKey) return null;
    const key = `checkout.validation.${field}.${errorKey}`;
    return t(key);
  };

  const handleSave = async () => {
    // Validate all fields before save
    setTouched({
      cardNumber: true,
      expirationDate: true,
      cvv: true,
      nameOnCard: true,
    });

    // Validate all fields
    const newErrors: typeof errors = {};
    const cardNumberDigits = cardNumber.replace(/\s/g, "").replace(/\*/g, "");

    // Card Number Validation
    if (!cardNumber || cardNumberDigits.length === 0) {
      newErrors.cardNumber = "REQUIRED";
    } else if (cardNumberDigits.length < 13 || cardNumberDigits.length > 19) {
      newErrors.cardNumber = "INVALID_LENGTH";
    } else if (!isEditMode && !validateCardNumber(cardNumber)) {
      newErrors.cardNumber = "INVALID_NUMBER";
    }

    // Expiration Date Validation
    const expValidation = validateExpirationDate(expirationDate);
    if (!expValidation.isValid) {
      newErrors.expirationDate = expValidation.error;
    }

    // CVV Validation
    const cvvValidation = validateCVV(cvv, detectedBrand);
    if (!cvvValidation.isValid) {
      newErrors.cvv = cvvValidation.error;
    }

    // Name on Card Validation (only if not edit mode or edit mode with new value)
    if (
      !isEditMode ||
      (isEditMode && nameOnCard !== editingPaymentMethod?.email)
    ) {
      const nameValidation = validateNameOnCard(nameOnCard);
      if (!nameValidation.isValid) {
        newErrors.nameOnCard = nameValidation.error;
      }
    }

    setErrors(newErrors);

    // If there are errors, don't save
    if (Object.keys(newErrors).length > 0) {
      console.log("Validation errors:", newErrors);
      return;
    }

    // Clear any previous errors before saving
    setErrors({});

    setIsSaving(true);

    // Parse expiration date (MM/YY)
    const [month, year] = expirationDate.split("/");
    const expMonth = parseInt(month, 10);
    const expYear = 2000 + parseInt(year, 10); // Convert YY to YYYY

    // Extract card number digits (remove spaces and asterisks)
    const last4 = cardNumberDigits.slice(-4);
    const binNumber = cardNumberDigits.slice(0, 8); // İlk 8 hane

    // Detect brand from card number (or use existing brand in edit mode)
    let brand = "Unknown";
    if (isEditMode && editingPaymentMethod) {
      // Edit mode'da mevcut brand'i kullan
      brand = editingPaymentMethod.brand;
    } else {
      const detected = getCardBrand(cardNumber);
      if (detected === "visa") brand = "Visa";
      else if (detected === "mastercard") brand = "Mastercard";
      else if (detected === "amex") brand = "American Express";
      else if (detected === "discover") brand = "Discover";
    }

    if (isEditMode && editingPaymentMethodId) {
      // Update existing payment method
      updatePaymentMethod(editingPaymentMethodId, {
        brand,
        last4,
        exp_month: expMonth,
        exp_year: expYear,
        email: nameOnCard,
        binNumber: binNumber.length === 8 ? binNumber : undefined, // İlk 8 hane
      });
    } else {
      // Add new payment method
      const newPaymentMethod = {
        id: `pm_${Date.now()}`,
        brand,
        last4,
        exp_month: expMonth,
        exp_year: expYear,
        email: nameOnCard,
        binNumber: binNumber.length === 8 ? binNumber : undefined, // İlk 8 hane
      };
      addPaymentMethod(newPaymentMethod);
    }

    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 500));
    setIsSaving(false);
    closePaymentMethodModal();
  };

  const handleCancel = () => {
    closePaymentMethodModal();
  };

  return (
    <Dialog
      open={isPaymentMethodModalOpen}
      onOpenChange={closePaymentMethodModal}
    >
      <DialogContent
        open={isPaymentMethodModalOpen}
        className="max-w-xl p-8 rounded-[20px] bg-white dark:bg-gray-900"
      >
        {/* Header - Title and Close Button */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-[24px] font-semibold leading-[32px] text-[#121926] dark:text-gray-100">
            {isEditMode
              ? t("checkout.editPaymentMethod")
              : t("checkout.addPaymentMethod")}
          </h2>
          <button
            onClick={handleCancel}
            className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            disabled={isSaving}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form Fields */}
        <div className="flex flex-col gap-4 mb-6">
          {/* Card Number */}
          <div className="flex flex-col gap-1">
            <Label
              htmlFor="cardNumber"
              className="text-[14px] font-medium leading-[20px] text-[#121926] dark:text-gray-100"
            >
              {t("checkout.cardNumber")}
            </Label>
            <div className="relative">
              <Input
                id="cardNumber"
                value={cardNumber}
                onChange={(e) => {
                  const input = e.target;
                  const cursorPosition = input.selectionStart || 0;

                  // Format card number with cursor position preservation
                  const { formattedValue, newCursorPosition } =
                    formatCardNumberWithCursor(e.target.value, cursorPosition);
                  setCardNumber(formattedValue);

                  // Cursor pozisyonunu koru
                  setTimeout(() => {
                    input.setSelectionRange(
                      newCursorPosition,
                      newCursorPosition
                    );
                  }, 0);

                  if (touched.cardNumber) {
                    validateField("cardNumber", formattedValue);
                  }
                }}
                onBlur={() => {
                  setTouched((prev) => ({ ...prev, cardNumber: true }));
                  validateField("cardNumber", cardNumber);
                }}
                placeholder="4444 4444 4444 4444"
                maxLength={19}
                className={`pr-12 bg-white dark:bg-gray-800 border rounded-[10px] px-3 py-[10px] text-[14px] ${
                  errors.cardNumber
                    ? "border-red-500 dark:border-red-400"
                    : "border-[#e3e8ef] dark:border-gray-700"
                }`}
                disabled={isSaving || isEditMode}
              />
              {detectedBrand && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <PaymentBrandLogo brand={detectedBrand} className="w-8 h-6" />
                </div>
              )}
            </div>
            {errors.cardNumber && (
              <p className="text-xs text-red-500 dark:text-red-400">
                {getErrorMessage("cardNumber", errors.cardNumber)}
              </p>
            )}
          </div>

          {/* Expiration Date and CVV */}
          <div className="flex gap-4">
            {/* Expiration Date */}
            <div className="flex-1 flex flex-col gap-1">
              <Label
                htmlFor="expirationDate"
                className="text-[14px] font-medium leading-[20px] text-[#121926] dark:text-gray-100"
              >
                {t("checkout.expirationDate")}
              </Label>
              <Input
                id="expirationDate"
                value={expirationDate}
                onChange={(e) => {
                  const formatted = formatExpirationDate(e.target.value);
                  setExpirationDate(formatted);
                  if (touched.expirationDate) {
                    validateField("expirationDate", formatted);
                  }
                }}
                onBlur={() => {
                  setTouched((prev) => ({ ...prev, expirationDate: true }));
                  validateField("expirationDate", expirationDate);
                }}
                placeholder="00/00"
                maxLength={5}
                className={`bg-white dark:bg-gray-800 border rounded-[10px] px-3 py-[10px] text-[14px] ${
                  errors.expirationDate
                    ? "border-red-500 dark:border-red-400"
                    : "border-[#e3e8ef] dark:border-gray-700"
                }`}
                disabled={isSaving}
              />
              {errors.expirationDate && (
                <p className="text-xs text-red-500 dark:text-red-400">
                  {getErrorMessage("expirationDate", errors.expirationDate)}
                </p>
              )}
            </div>

            {/* CVV */}
            <div className="flex-1 flex flex-col gap-1">
              <Label
                htmlFor="cvv"
                className="text-[14px] font-medium leading-[20px] text-[#121926] dark:text-gray-100"
              >
                {t("checkout.cvv")}
              </Label>
              <div className="relative">
                <Input
                  id="cvv"
                  type="password"
                  value={cvv}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    const formatted = value.slice(0, 4);
                    setCvv(formatted);
                    if (touched.cvv) {
                      validateField("cvv", formatted);
                    }
                  }}
                  onBlur={() => {
                    setTouched((prev) => ({ ...prev, cvv: true }));
                    validateField("cvv", cvv);
                  }}
                  placeholder="000"
                  maxLength={4}
                  className={`pr-8 bg-white dark:bg-gray-800 border rounded-[10px] px-3 py-[10px] text-[14px] ${
                    errors.cvv
                      ? "border-red-500 dark:border-red-400"
                      : "border-[#e3e8ef] dark:border-gray-700"
                  }`}
                  disabled={isSaving || isEditMode}
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <HelpCircle className="w-4 h-4 text-gray-400" />
                </div>
              </div>
              {errors.cvv && (
                <p className="text-xs text-red-500 dark:text-red-400">
                  {getErrorMessage("cvv", errors.cvv)}
                </p>
              )}
            </div>
          </div>

          {/* Name on Card */}
          <div className="flex flex-col gap-1">
            <Label
              htmlFor="nameOnCard"
              className="text-[14px] font-medium leading-[20px] text-[#121926] dark:text-gray-100"
            >
              {t("checkout.nameOnCard")}
            </Label>
            <Input
              id="nameOnCard"
              value={nameOnCard}
              onChange={(e) => {
                setNameOnCard(e.target.value);
                if (touched.nameOnCard) {
                  validateField("nameOnCard", e.target.value);
                }
              }}
              onBlur={() => {
                setTouched((prev) => ({ ...prev, nameOnCard: true }));
                validateField("nameOnCard", nameOnCard);
              }}
              placeholder={t("checkout.nameOnCardPlaceholder")}
              className={`bg-white dark:bg-gray-800 border rounded-[10px] px-3 py-[10px] text-[14px] ${
                errors.nameOnCard
                  ? "border-red-500 dark:border-red-400"
                  : "border-[#e3e8ef] dark:border-gray-700"
              }`}
              disabled={isSaving}
            />
            {errors.nameOnCard && (
              <p className="text-xs text-red-500 dark:text-red-400">
                {getErrorMessage("nameOnCard", errors.nameOnCard)}
              </p>
            )}
          </div>
        </div>

        {/* Save Card Details Checkbox */}
        <div className="flex items-center gap-2 mb-6">
          <Checkbox
            id="saveCardDetails"
            checked={saveCardDetails}
            onCheckedChange={(checked) =>
              setSaveCardDetails(checked as boolean)
            }
            className="rounded-[4px] border-[#6172f3] data-[state=checked]:bg-[#6172f3] data-[state=checked]:border-[#6172f3]"
            disabled={isSaving}
          />
          <Label
            htmlFor="saveCardDetails"
            className="text-[14px] font-medium leading-[20px] text-[#121926] dark:text-gray-100 cursor-pointer"
          >
            {t("checkout.saveCardDetails")}
          </Label>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-6">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isSaving}
            className="flex-1 bg-white dark:bg-gray-800 border border-[#e3e8ef] dark:border-gray-700 text-[#414651] dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-[8px] px-[14px] py-[10px] text-[14px] font-semibold"
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleSave}
            disabled={
              isSaving ||
              !cardNumber ||
              cardNumber.replace(/\s/g, "").replace(/\*/g, "").length === 0 ||
              !expirationDate ||
              expirationDate.length < 5 ||
              (!isEditMode && (!cvv || cvv.length === 0)) ||
              !nameOnCard ||
              nameOnCard.trim().length === 0
            }
            className="flex-1 bg-[#0e121b] hover:bg-[#0e121b]/90 dark:bg-[#0e121b] dark:hover:bg-[#0e121b]/90 text-white rounded-[10px] px-4 py-[10px] font-semibold text-[14px] leading-[20px] shadow-[0px_1px_2px_0px_rgba(27,28,29,0.48),0px_0px_0px_1px_#242628] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? t("common.saving") : t("common.save")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
